/**
 * Owns the "Preview Script" round-trip so `CloneTableDialog` doesn't grow
 * another block of state (it's already well over the file-size guideline).
 */

import { useCallback, useRef, useState } from 'react';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import type { CloneTableRequest } from '../types/cloneTable';

export type ClonePreviewState = {
  open: boolean;
  isLoading: boolean;
  script?: string;
  error?: string;
};

const CLOSED: ClonePreviewState = { open: false, isLoading: false };

export function useClonePreviewScript() {
  const [previewState, setPreviewState] = useState<ClonePreviewState>(CLOSED);
  const { track } = useAnalytics();
  /**
   * Which request the dialog is currently showing. Metadata fetches can take a
   * while on a cold connection, so a response is only allowed to write state
   * while it is still the newest one — otherwise closing the dialog mid-load
   * pops it back open when the IPC lands, and two queued previews can resolve
   * out of order and show the older script.
   */
  const latestRequestRef = useRef(0);

  const requestPreview = useCallback(
    async (request: CloneTableRequest) => {
      const requestId = ++latestRequestRef.current;
      const applyIfCurrent = (next: ClonePreviewState): void => {
        if (latestRequestRef.current !== requestId) return;
        setPreviewState(next);
      };

      setPreviewState({ open: true, isLoading: true });
      track('feature:clone_table_preview', { engine: request.engine });

      if (!window.electronAPI?.cloneTable?.previewScript) {
        applyIfCurrent({ open: true, isLoading: false, error: 'Clone Table API is not available' });
        return;
      }

      try {
        const result = await window.electronAPI.cloneTable.previewScript(request);
        applyIfCurrent({
          open: true,
          isLoading: false,
          script: result.script,
          error: result.success ? undefined : result.error || 'Could not generate the script',
        });
      } catch (err) {
        applyIfCurrent({
          open: true,
          isLoading: false,
          error: err instanceof Error ? err.message : 'Unknown error generating the script',
        });
      }
    },
    [track],
  );

  const setPreviewOpen = useCallback((open: boolean) => {
    // Closing also retires the in-flight request, so a late response can't
    // resurrect the dialog the user just dismissed.
    if (!open) latestRequestRef.current++;
    setPreviewState((prev) => (open ? { ...prev, open } : CLOSED));
  }, []);

  return { previewState, requestPreview, setPreviewOpen };
}
